import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import { ReactNode } from 'react';
import { SectionBox } from './helpers';
import { Button, Typography, colors } from '@sweep-io/sweep-design';
import { ExpandButtonSimple } from '../../common/ExpandButtonSimple';
import { ConfigurationType } from './types';
import { DotDivider } from '../../common/DotDivider';
import { RelatedListTable } from '../configuration-item-tables/RelatedListTable';
import { humanizeDate } from '../../helpers/humanizeDate';
import Linkify from 'linkify-react';
import { ConfigurationItem } from '../../parser/ParserTypes';
import { useSearchContext } from './search/SearchContext';
import { HighlightedSection } from './search/highlight/HighlightedSection';
import { useSectionExpansion } from './search/useSectionExpansion';

interface DetailsSectionProps {
  configurationItem: ConfigurationItem;
  isField?: boolean;
  isExpandedByDefault?: boolean;
}

const DETAILS_TEXT_COLOR = colors.grey[700];

export const DetailsSection = ({
  isField,
  configurationItem,
  isExpandedByDefault,
}: DetailsSectionProps) => {
  // Common properties available on all configuration items
  const { description, helpText, name, createdAt } = configurationItem;

  // Type-specific properties accessed safely
  const flowDescription =
    'flowDescription' in configurationItem ? configurationItem.flowDescription : undefined;
  const formula = 'formula' in configurationItem ? configurationItem.formula : undefined;
  const isMandatory =
    'isMandatory' in configurationItem ? configurationItem.isMandatory : undefined;
  const buttons = 'buttons' in configurationItem ? configurationItem.buttons : undefined;
  const relatedLists =
    'relatedLists' in configurationItem ? configurationItem.relatedLists : undefined;

  const { searchTerm, sectionExpansion } = useSearchContext();

  const { isExpanded, setIsExpanded } = useSectionExpansion({
    searchTerm,
    sectionExpanded: sectionExpansion.details,
    defaultExpanded: !!isExpandedByDefault,
  });

  const isRecordType = configurationItem.parentType === ConfigurationType.recordTypes;
  const isEmptyField = !helpText && !name && !description && !createdAt;
  const isEmptyRecordType = isRecordType && !name && !description && !createdAt;

  const isLayout = configurationItem.parentType === ConfigurationType.layouts;
  const isEmptyLayout = !buttons?.length && !relatedLists?.length && !createdAt;

  const isEmptyConfiguration = !flowDescription && !description && !formula && !createdAt;

  const isEmpty = isField
    ? isEmptyField
    : isRecordType
      ? isEmptyRecordType
      : isLayout
        ? isEmptyLayout
        : isEmptyConfiguration;

  const shouldDisplayApiName = name && (isField || isRecordType);

  if (isEmpty) {
    return null;
  }

  return (
    <SectionBox data-section="details">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        onClick={() => {
          setIsExpanded(!isExpanded);
        }}
        sx={{ cursor: 'pointer' }}
      >
        <Typography variant="h5-medium">Details</Typography>
        <ExpandButtonSimple buttonSize="tiny" isExpanded={isExpanded} />
      </Box>

      <Collapse in={isExpanded}>
        <HighlightedSection section="details">
          <Box pt={'12px'} gap="12px" display="inline-flex" width="100%" flexDirection="column">
            {shouldDisplayApiName && (
              <DetailsItem headerTxt="API Name" contentTxt={name} isMandatory={isMandatory} />
            )}
            {description && <DetailsItem headerTxt="Description" contentTxt={description} />}
            {createdAt && (
              <DetailsItem
                headerTxt="Creation date"
                contentTxt={humanizeDate({ dateOrTimestamp: createdAt })}
              />
            )}
            {helpText && isField && <DetailsItem headerTxt="Help Text" contentTxt={helpText} />}
            {flowDescription && (
              <DetailsItem headerTxt="Flow description" contentTxt={flowDescription} />
            )}
            {formula && (
              <DetailsWrapperWithHeader headerTxt="Formula">
                <Box component="pre" sx={{ whiteSpace: 'pre-wrap', m: 0 }}>
                  <Typography variant="caption" color={DETAILS_TEXT_COLOR}>
                    {formula}
                  </Typography>
                </Box>
              </DetailsWrapperWithHeader>
            )}
            {isLayout && (
              <>
                {!!buttons?.length && (
                  <DetailsItem
                    headerTxt="Buttons"
                    contentTxt={buttons.map((button: { label: string }) => button.label).join(', ')}
                  />
                )}
                {!!relatedLists?.length && (
                  <RelatedListTable
                    tableHeader="Related list"
                    items={relatedLists}
                    section="details"
                  />
                )}
              </>
            )}
          </Box>
        </HighlightedSection>
      </Collapse>
    </SectionBox>
  );
};

const DetailsItem = ({
  headerTxt,
  contentTxt,
  isMandatory,
}: {
  headerTxt: string;
  contentTxt: string;
  isMandatory?: boolean;
}) => (
  <DetailsWrapperWithHeader headerTxt={headerTxt}>
    <Linkify
      options={{
        target: 'blank',
        validate: {
          url: (value) =>
            /\b(?:https?:\/\/(?:www\.)?|www\.)[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}\b/.test(
              value,
            ),
        },
        render: ({ attributes, content }) => {
          const { href } = attributes;
          return (
            <Box display="inline-flex" alignItems="center">
              <Button variant="flat-blue" size="tiny" onClick={() => window.open(href, '_blank')}>
                {content}
              </Button>
            </Box>
          );
        },
      }}
    >
      <Typography variant="caption" color={DETAILS_TEXT_COLOR} wordBreak="break-word">
        {contentTxt}
        {isMandatory ? (
          <>
            <Box component="span" sx={{ margin: '0 8px' }}>
              •
            </Box>
            <Typography variant="caption" color={colors.blush[600]}>
              Mandatory
            </Typography>
          </>
        ) : (
          ''
        )}
      </Typography>
    </Linkify>
  </DetailsWrapperWithHeader>
);

const DetailsWrapperWithHeader = ({
  headerTxt,
  children,
}: {
  headerTxt: string;
  children: ReactNode;
}) => (
  <Box display="flex" flexDirection="column" gap={1}>
    <Box display="flex" gap={1} alignItems="center">
      <DotDivider dotColor={colors.grey[900]} />
      <Typography variant="body" color={colors.grey[900]}>
        {headerTxt}
      </Typography>
    </Box>
    <Box pl={2} display="inline-flex" alignItems="center">
      {children}
    </Box>
  </Box>
);
